import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutsideClickEffect } from '@saviynt/common';
import {
  Box,
  ButtonCore,
  Icon,
  InlineMessage,
  InputField,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import useDebounceForFunctions from '../../../../hooks/use-debounceforfunction';
import { setSearchText } from '../../../../redux/certlist/action';
import { useInvertedLocalize } from '../../../../utils/localization';

import { DELAY_TIMEOUT } from './constant';
import { invertedMessages } from './messages';

import './SearchBar.css';

function SearchBar({ isSearchBarExpanded, setIsSearchBarExpanded }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchAlert, setIsSearchAlert] = useState(false);
  const [isTimeOutForExpanded, setIsTimeOutForExpanded] = useState(false);

  const searchBarButtonRef = useRef(null);
  const searchBarInputRef = useRef(null);
  const dispatch = useDispatch();

  const {
    global: { isGlobalLoading, totalItemsCount },
    viewControls: { searchText },
  } = useSelector((state) => state?.certifications?.list);
  const l = useInvertedLocalize(invertedMessages);

  const searchBarClasses = classnames(
    'CertListSearchBar',
    isTimeOutForExpanded && 'CertListSearchBar--isExpanded',
    (totalItemsCount === 0 || isGlobalLoading) &&
      'CertListSearchBar--isDisabled'
  );

  const searchBarButtonClasses = classnames(
    'CertListSearchBar-button',
    (totalItemsCount === 0 || isGlobalLoading) &&
      'CertListSearchBar-button--isDisabled'
  );

  useEffect(() => {
    setSearchQuery(searchText);
  }, [searchText]);

  useEffect(() => {
    if (searchQuery) {
      setIsSearchBarExpanded(true);
      setIsTimeOutForExpanded(true);
    }
  }, [searchQuery]);

  const handleSearchExpand = () => {
    if (searchQuery) return;

    setIsSearchAlert(false);
    const inputField = searchBarInputRef?.current;

    if (isSearchBarExpanded) {
      setIsTimeOutForExpanded(false);
      inputField.blur();

      setTimeout(() => {
        setIsSearchBarExpanded(false);
      }, 200);
    } else {
      inputField?.focus();
      setIsSearchBarExpanded(true);
      setIsTimeOutForExpanded(true);
    }
  };

  useOutsideClickEffect(
    searchBarInputRef,
    () => {
      handleSearchExpand();
    },
    isTimeOutForExpanded
  );

  const updateSearchText = useCallback((searchTermValue) => {
    const isSearchTextValid = Boolean(
      searchTermValue.trim().length === 0 || searchTermValue.trim().length > 2
    );

    if (!isSearchTextValid) {
      setIsSearchAlert(true);

      return;
    }

    dispatch(setSearchText(searchTermValue.trim())); // update searchText in redux state
  }, []);

  const debounceHandler = useCallback(
    useDebounceForFunctions(updateSearchText, DELAY_TIMEOUT),
    [updateSearchText]
  );

  // Handle input change
  const handleChange = (event) => {
    const isSearchTextValid = Boolean(
      event.target.value.trim().length === 0 ||
        event.target.value.trim().length > 2
    );

    setSearchQuery(event.target.value); // Update input field value
    debounceHandler(event.target.value); // Call the debounced function

    if (isSearchAlert && isSearchTextValid) {
      setIsSearchAlert(false);
    }
  };

  const resetSearch = () => {
    setSearchQuery('');
    setIsSearchAlert(false);
    dispatch(setSearchText(''));
    handleSearchExpand();
  };

  const handleSearchBoxClose = () => {
    if (searchQuery.trim().length !== 0) {
      resetSearch();

      return;
    }

    handleSearchExpand();
  };

  return (
    <Box tag='div' className={searchBarClasses}>
      <InputField
        ref={searchBarInputRef}
        name='SearchBar'
        placeholder=''
        kind='search'
        size='medium'
        prefixIcon={<Icon kind='search' color='neutral-1000' size='xLarge' />}
        value={searchQuery}
        setValue={setSearchQuery}
        onChange={handleChange}
        resetValue={handleSearchBoxClose}
        isDisabled={totalItemsCount === 0 || isGlobalLoading}
        tabIndex={isSearchBarExpanded ? 0 : -1}
      />
      <ButtonCore
        ref={searchBarButtonRef}
        className={searchBarButtonClasses}
        onClick={handleSearchExpand}
        tabIndex={!isSearchBarExpanded ? 0 : -1}
        isDisabled={totalItemsCount === 0 || isGlobalLoading}
        ariaLabel={l('Search')}
      />
      {isSearchAlert && isSearchBarExpanded && (
        <InlineMessage
          className='CertListSearchBar-warning'
          text={l('Type more than 2 characters to see results.')}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      )}
      <span className='CertListSearchBar-overlay' />
    </Box>
  );
}

SearchBar.propTypes = {
  isSearchBarExpanded: PropTypes.bool.isRequired,
  setIsSearchBarExpanded: PropTypes.func.isRequired,
};

export default SearchBar;
